<template>
  <div class="animated fadeIn">
    <b-row>
      <PageTitle></PageTitle>
      <filter-area
        @filterData="filterData()"
        @resetData="resetFilerAreaData()"
      >
        <b-row>
          <b-col cols="4">
            <b-form-group>
              <label for="activeRadio">{{$t('filterArea.label.state')}}</label>
              <b-form-radio-group id="activeRadio"
                :plain="true"
                :options="activeRadioOptions"
                v-model="filterArea.state">
              </b-form-radio-group>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group>
              <label for="code">{{$t('filterArea.label.code')}}</label>
              <b-form-input
                type="text"
                id="code"
                :placeholder="$t('filterArea.label.code')"
                v-model="filterArea.code">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group>
              <label for="name">{{$t('filterArea.label.name')}}</label>
              <b-form-input
                type="text"
                id="name"
                :placeholder="$t('filterArea.label.name')"
                v-model="filterArea.name">
              </b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <b-form-group>
              <label for="code">{{$t('filterArea.label.category')}}</label>
              <v-select v-model="filterArea.categories" :reduce="category => category.code" multiple :options="categories"></v-select>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group>
              <label for="name">{{$t('filterArea.label.format')}}</label>
              <v-select
                v-model="filterArea.format"
                :options="formats"
              ></v-select>
            </b-form-group>
          </b-col>
        </b-row>
      </filter-area>
      <b-col lg="12">
        <b-card>
          <b-table
            :stacked="stacked"
            :hover="hover"
            :striped="striped"
            :bordered="bordered"
            :small="small"
            :fixed="fixed"
            :items="items"
            :fields="captions"
            :current-page="1"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            :per-page="perPage"
            @sort-changed="sortingChanged"
            :no-sort-reset="true"
            :no-local-sorting="true">
            <template slot="actions" slot-scope="data">
              <div class="btn-group" role="group">
                <router-link tag="button" class="btn btn-primary fa fa-search" v-b-tooltip.hover :title="$t('actions.show')"
                             :to="{name: 'modelShowRoute', params:{id: data.item.id}}">
                </router-link>
                <router-link tag="button" class="btn btn-warning fa fa-copy" v-b-tooltip.hover :title="$t('actions.clone')"
                             :to="{name: 'modelCloneRoute', params:{id: data.item.id}}">
                </router-link>
                <router-link tag="button" class="btn btn-success far fa-play" v-b-tooltip.hover :title="$t('actions.simulate')"
                             :to="{name: 'modelSimulateRequestRoute', params:{code: data.item.code}}">
                </router-link>
                <router-link tag="button" class="btn btn-warning fa fa-edit" v-b-tooltip.hover :title="$t('actions.edit')"
                             :to="{name: 'modelEditRoute', params:{id: data.item.id}}">
                </router-link>
              </div>
            </template>
            <template slot="enabled" slot-scope="data">
              <c-switch
                class="mx-1"
                @change="updateStatus(data.item.id, data.item.enabled)"
                v-model="data.item.enabled"
                color="success"
                variant="pill"
                :disabled="data.item.companyTotal > 0"
                :title ="getTitle(data.item.enabled, data.item.companyTotal)"
                label/>
            </template>
            <template slot="categories" slot-scope="data">
              <b-badge v-for="category in data.item.categories" :key="category.id" :variant="`warning`" class="mx-1">
                <router-link class="cat" :to="{path: `/category/show/${category.id}`}">
                  {{category.code}}
                </router-link>
              </b-badge>
            </template>
            <template slot="companyTotal" slot-scope="data">
              <a v-if="data.item.companyTotal > 0" href="#" style="cursor: pointer;" @click="showCompanyList(data.item.id)">{{ data.item.companyTotal }}</a>
              <span v-else>{{ data.item.companyTotal }}</span>
            </template>
          </b-table>
          <div class="row">
            <div class="col-sm-4">
              <b-pagination :total-rows="rowCount" :per-page="perPage" v-model="currentPage"
                            :prev-text="$t('actions.previous')"
                            :next-text="$t('actions.next')"
                            hide-goto-end-buttons @input="fetchData(currentPage)"/>
            </div>
            <div class="col-sm-6"></div>
            <div class="col-sm-2">
              <router-link class="btn btn-primary btn-block float-right" role="button" :to="{path: `/models/new`}" push>
                {{ $t('actions.create') }}
              </router-link>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row><!--/.row-->
    <b-modal :title="$t('page.title.list', 0, {n: $tc('page.generic.utilizer', 0)})" scrollable size="xl" v-model="companyListModal" ok-only>
      <b-table
        :stacked="stacked"
        :hover="hover"
        :striped="striped"
        :bordered="bordered"
        :small="small"
        :fixed="fixed"
        :items="companyItems"
        :fields="companyCaptions">
        <template slot="active" slot-scope="data">
          <b-badge :variant="getBadge(data.item.active)">{{activeDesc(data.item.active)}}</b-badge>
        </template>
        <template slot="mandatory" slot-scope="data">
          <b-badge :variant="getBadge(data.item.mandatory)">{{mandatoryDesc(data.item.mandatory)}}</b-badge>
        </template>
        <template slot="description" slot-scope="data">
          <span>{{data.item.description}}</span>
        </template>
      </b-table>
    </b-modal>
  </div>
</template>
<script>
  import PageTitle from '@/components/PageTitle'
  import ModelService from '@/services/ModelService'
  import DropdownList from '@/components/ListDropdown'
  import i18n from '@/config/i18n'
  import CustomSwitch from '../../components/CustomSwitch'
  import FilterArea from '../../components/FilterArea'
  import miniToastr from 'mini-toastr' //ES6
  import vSelect from 'vue-select'

  const modelService = ModelService.build()

  export default {
    name: 'modelList',
    inheritAttrs: false,
    components: {
      PageTitle,
      DropdownList,
      cSwitch: CustomSwitch,
      FilterArea,
      vSelect
    },
    props: {
      caption: {
        type: String,
        default: 'Modelli'
      },
      stacked: {
        type: String,
        default: 'sm'
      },
      hover: {
        type: Boolean,
        default: true
      },
      striped: {
        type: Boolean,
        default: false
      },
      bordered: {
        type: Boolean,
        default: false
      },
      small: {
        type: Boolean,
        default: false
      },
      fixed: {
        type: Boolean,
        default: false
      },
      perPage: {
        type: Number,
        default: 7
      }
    },
    data: () => {
      return {
         fields: [
          {
            key: 'enabled',
            sortable: true,
            label: 'Stato',
          },
          {
            key: 'code',
            sortable: true,
            label: i18n.t('page.generic.code')
          },
          {
            key: 'modelVersion',
            sortable: true,
            label: i18n.t('page.generic.version'),
            formatter: 'versionFormatter'
          },
          {
            key: 'label',
            sortable: true,
            label: i18n.t('page.generic.name')
          },
          {
            key: 'categories',
            sortable: false,
            label: i18n.tc('title.categories', 0)
          },
          {
            key: 'modelFormat',
            sortable: true,
            label: i18n.tc('page.generic.format', 1)
          },
          {
            key: 'companyTotal',
            sortable: false,
            label: i18n.tc('page.generic.utilizer', 0)
          },
          {
            key: 'actions',
            sortable: false,
            label: ''
          }
        ],
        companyFields: [
          {
            key: 'active',
            sortable: true,
            label: i18n.tc('page.generic.state', 1)
          },
          {
            key: 'code',
            sortable: true,
            label: i18n.t('page.generic.code')
          },
          {
            key: 'label',
            sortable: true,
            label: i18n.t('page.generic.name')
          },
          {
            key: 'description',
            sortable: true,
            label: i18n.t('page.generic.description')
          },
          {
            key: 'mandatory',
            sortable: true,
            label: 'Modello Obbligatorio'
          },
        ],
        currentPage: 1,
        tableData: [],
        tableTotalRows: 0,
        sortBy: 'code',
        sortDesc: false,
        sortDirection: 'asc',
        filter: null,
        companyListModal: false,
        companies: [],
        activeRadioOptions: [
          {text: 'Attivo',value: true},
          {text: 'Disattivo',value: false},
        ],
        filterArea: {},
        formats:[],
        categories:[]
      }
    },
    created() {
      miniToastr.init(); // config can be passed here miniToastr.init(config)
      miniToastr.setIcon('error', 'i', {'class': 'fas fa-exclamation-triangle'});
      this.fetchData(1);
      this.getFormats();
      this.getCategories();
      this.resetFilerAreaData()
    },
    computed: {
      items: function () {
        const items = this.tableData;
        return Array.isArray(items) ? items : []
      },
      captions: function () {
        return this.fields
      },
      rowCount: function () {
        return this.tableTotalRows
      },
      companyItems: function () {
        const items = this.companies;
        return Array.isArray(items) ? items : []
      },
      companyCaptions: function () {
        return this.companyFields
      },
      filterIconClass: function(){
        return this.isCollapsed ? 'fa-chevron-up' : 'fa-chevron-down'
      },
      categoryFilters: function(){
        if(this.filterArea.categories !== null){
          return this.filterArea.categories.map(a => a.code).join(",");
        }
      }
    },
    methods: {
      versionFormatter(value) {
        return `V-${value < 10 ? '0' + value : value}`
      },
      getBadge(active) {
        return active ? 'success' : 'danger'
      },
      activeDesc: function (value) {
        return value ? i18n.tc('page.generic.enabled') : i18n.tc('page.generic.disabled')
      },
      mandatoryDesc: function (value) {
        return value ? i18n.tc('page.generic.yes') : i18n.tc('page.generic.no')
      },
      async updateStatus(id, status) {
        try {
          await modelService.update({id: id, enabled: status})
          miniToastr.info(`Modello ${status?'abilitato':'disabilitato'} con successo`);
        } catch (exception) {
          miniToastr.error(exception.message)
        }
      },
      async fetchData(currentPage) {
        (
          {
            data: this.tableData = [],
            total: this.tableTotalRows = 0
          } = await modelService.search(
            {
              max: this.perPage,
              offset: (currentPage - 1) * this.perPage,
              sort: this.sortBy,
              order: (this.sortDesc ? 'desc' : 'asc'),
              enabled: this.filterArea.state,
              code: this.filterArea.code,
              name: this.filterArea.name,
              fCategories: this.categoryFilters,
              modelFormat: this.filterArea.format
            }
          )
        )

      },
      sortingChanged(ctx) {
        // ctx.sortBy   ==> Field key for sorting by (or null for no sorting)
        // ctx.sortDesc ==> true if sorting descending, false otherwise
        this.sortBy = ctx.sortBy;
        this.sortDesc = ctx.sortDesc;
        this.fetchData(this.currentPage)
      },
      showCompanyList(modelId) {
        let model = this.tableData.filter(element => element.id === modelId);
        this.companies = model[0].companies ? model[0].companies : [];
        this.companyListModal = true;
      },
      filterData(){
        this.fetchData(1)
      },
      resetFilerAreaData(){
        this.filterArea = {
          state : null,
          code: null,
          name: null,
          categories: null,
          format: null
        };
        this.fetchData(1)
      },
      getFormats() {
        this.$http.get(`api/models/formats`)
          .then(response => response.json())
          .then(json => {
            this.formats = json.map(function (format) {
              return format.name
            })
          })
      },
      getCategories() {
        this.$http.get(`api/categories/`)
          .then(response => response.json())
          .then(json => {
            this.categories = json.data
          })
      },
      getTitle(status, companies){
        let title;
        if(companies > 0){
          title = i18n.t('model.noDisabledTitle')
        }else if(status && companies <= 0){
          title = i18n.t('model.disabledTitle')
        }else{
          title = i18n.t('model.enabledTitle')
        }
        return title
      }
    }
  }
</script>

<style type="text/scss" scoped>
  .cat {
    text-underline: none !important;
    color: #000 !important;
  }

  .cat:hover {
    text-underline: none !important;
    color: #000 !important;
  }

  a {
    text-decoration: none;

    &:hover {
      text-decoration: none;
    }
  }
</style>

